/**
 * User Detail Screen
 *
 * Detalhes de um usuário específico
 */
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
	ArrowLeft,
	Mail,
	Phone,
	Calendar,
	Building2,
	Star,
	Shield,
	Trash2,
	MapPin,
} from 'lucide-react'
import Header from '../organisms/Header'
import Card from '../atoms/Card'
import Badge from '../atoms/Badge'
import Button from '../atoms/Button'
import Avatar from '../atoms/Avatar'
import { LoadingScreen } from '../atoms/Spinner'
import { Modal, ConfirmModal } from '../molecules/Modal'
import Select from '../atoms/Select'
import { usersApi } from '../../core/admin.api'
import { ADMIN_ROUTES, USER_ROLE_LABELS, USER_ROLE_COLORS } from '../../core/admin.constants'
import { formatDate, formatPhoneNumber } from '../../core/admin.utils'
import toast from 'react-hot-toast'

export default function UserDetailScreen() {
	const { id } = useParams()
	const navigate = useNavigate()
	const [user, setUser] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [showRoleModal, setShowRoleModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [newRole, setNewRole] = useState('user')
	const [actionLoading, setActionLoading] = useState(false)

	const fetchUser = useCallback(async () => {
		try {
			setLoading(true)
			const data = await usersApi.getById(id)
			// usersApi.getById já retorna os dados diretamente
			setUser(data)
			setNewRole(data?.role || 'user')
		} catch (err) {
			setError(err.response?.data?.message || 'Erro ao carregar usuário')
		} finally {
			setLoading(false)
		}
	}, [id])

	useEffect(() => {
		fetchUser()
	}, [fetchUser])

	const handleChangeRole = async () => {
		setActionLoading(true)
		try {
			await usersApi.updateRole(id, newRole)
			toast.success(`Tipo alterado para ${USER_ROLE_LABELS[newRole]}`)
			setShowRoleModal(false)
			fetchUser()
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao alterar tipo')
		} finally {
			setActionLoading(false)
		}
	}

	const handleDelete = async () => {
		setActionLoading(true)
		try {
			await usersApi.delete(id)
			toast.success('Usuário excluído com sucesso')
			navigate(ADMIN_ROUTES.USERS)
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao excluir usuário')
		} finally {
			setActionLoading(false)
		}
	}

	if (loading) {
		return (
			<div className="min-h-screen">
				<Header title="Carregando..." />
				<LoadingScreen />
			</div>
		)
	}

	if (error || !user) {
		return (
			<div className="min-h-screen">
				<Header title="Erro" />
				<div className="p-6">
					<Card className="text-center py-12">
						<p className="text-red-600 mb-4">{error || 'Usuário não encontrado'}</p>
						<Button onClick={() => navigate(ADMIN_ROUTES.USERS)}>
							Voltar
						</Button>
					</Card>
				</div>
			</div>
		)
	}

	return (
		<div className="min-h-screen">
			<Header
				title="Detalhes do Usuário"
				actions={
					<Button
						variant="outline"
						size="sm"
						onClick={() => navigate(ADMIN_ROUTES.USERS)}
					>
						<ArrowLeft className="w-4 h-4 mr-2" />
						Voltar
					</Button>
				}
			/>

			<div className="p-6 space-y-6">
				{/* Header do usuário */}
				<Card className="p-6">
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
						<div className="flex items-center gap-4">
							<Avatar
								src={user.photo}
								name={user.name}
								size="xl"
							/>
							<div>
								<h2 className="text-xl font-bold text-text">
									{user.name} {user.lastname || ''}
								</h2>
								<p className="text-text-body">{user.email}</p>
								<div className="flex items-center gap-2 mt-2">
									<Badge variant={USER_ROLE_COLORS[user.role] || 'gray'}>
										{USER_ROLE_LABELS[user.role] || 'Usuário'}
									</Badge>
									{user.club && (
										<Badge variant="blue">
											<Building2 className="w-3 h-3 mr-1" />
											{user.club.name}
										</Badge>
									)}
								</div>
							</div>
						</div>

						<div className="flex gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setShowRoleModal(true)}
							>
								<Shield className="w-4 h-4 mr-2" />
								Alterar Tipo
							</Button>
							<Button
								variant="danger"
								size="sm"
								onClick={() => setShowDeleteModal(true)}
							>
								<Trash2 className="w-4 h-4 mr-2" />
								Excluir
							</Button>
						</div>
					</div>
				</Card>

				{/* Informações */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* Dados pessoais */}
					<Card className="p-6">
						<h3 className="text-lg font-semibold mb-4">Dados Pessoais</h3>
						<div className="space-y-4">
							<InfoRow
								icon={Mail}
								label="E-mail"
								value={user.email}
							/>
							<InfoRow
								icon={Phone}
								label="Telefone"
								value={user.whatsapp || user.phone ? formatPhoneNumber(user.whatsapp || user.phone) : 'Não informado'}
							/>
							<InfoRow
								icon={Calendar}
								label="Data de Nascimento"
								value={user.birthDate ? formatDate(user.birthDate, 'dd/MM/yyyy') : 'Não informado'}
							/>
							<InfoRow
								icon={MapPin}
								label="Cidade"
								value={user.city || 'Não informado'}
							/>
						</div>
					</Card>

					{/* Estatísticas */}
					<Card className="p-6">
						<h3 className="text-lg font-semibold mb-4">Estatísticas</h3>
						<div className="space-y-4">
							<InfoRow
								icon={Calendar}
								label="Cadastro em"
								value={formatDate(user.createdAt, "dd/MM/yyyy 'às' HH:mm")}
							/>
							<InfoRow
								icon={Star}
								label="Avaliação média"
								value={user.averageRating ? `${user.averageRating.toFixed(1)} estrelas` : 'Sem avaliações'}
							/>
							<InfoRow
								icon={Building2}
								label="Partidas jogadas"
								value={user.matchesPlayed || 0}
							/>
						</div>
					</Card>
				</div>

				{/* Clube */}
				{user.club && (
					<Card className="p-6">
						<h3 className="text-lg font-semibold mb-4">Clube</h3>
						<div
							className="flex items-center gap-4 p-4 bg-base-400 rounded-lg cursor-pointer hover:bg-base-200 transition-colors"
							onClick={() => navigate(`${ADMIN_ROUTES.CLUBS}/${user.club._id}`)}
						>
							<Avatar
								src={user.club.photo}
								name={user.club.name}
								size="lg"
							/>
							<div>
								<p className="font-medium text-text">{user.club.name}</p>
								{user.club.address && (
									<p className="text-sm text-text-body">
										{user.club.address.city}, {user.club.address.state}
									</p>
								)}
							</div>
						</div>
					</Card>
				)}
			</div>

			{/* Modal de alterar tipo */}
			<Modal
				isOpen={showRoleModal}
				onClose={() => setShowRoleModal(false)}
				title="Alterar Tipo de Usuário"
			>
				<div className="space-y-4">
					<Select
						label="Novo tipo"
						value={newRole}
						onChange={(e) => setNewRole(e.target.value)}
						options={[
							{ value: 'user', label: 'Usuário' },
							{ value: 'admin', label: 'Admin' },
							{ value: 'superadmin', label: 'Super Admin' },
						]}
					/>

					<div className="flex justify-end gap-3 mt-6">
						<Button
							variant="outline"
							onClick={() => setShowRoleModal(false)}
						>
							Cancelar
						</Button>
						<Button
							onClick={handleChangeRole}
							loading={actionLoading}
						>
							Salvar
						</Button>
					</div>
				</div>
			</Modal>

			{/* Modal de confirmação de exclusão */}
			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleDelete}
				title="Excluir Usuário"
				message={`Tem certeza que deseja excluir o usuário "${user.name}"? Esta ação não pode ser desfeita.`}
				confirmText="Excluir"
				variant="danger"
				loading={actionLoading}
			/>
		</div>
	)
}

// Componente auxiliar para linha de informação
function InfoRow({ icon: Icon, label, value }) {
	return (
		<div className="flex items-start gap-3">
			<div className="p-2 bg-base-400 rounded-lg">
				<Icon className="w-4 h-4 text-text-body" />
			</div>
			<div>
				<p className="text-sm text-text-body">{label}</p>
				<p className="font-medium text-text">{value}</p>
			</div>
		</div>
	)
}
